import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, Query } from '@angular/fire/firestore';
import { Constant } from '@app/@core';
import { UserDetail } from '@app/interface/auth.user';
import { consentFormPage, StudyModel } from '@app/interface/study.model';
import { SurveyQuestion } from '@app/interface/survey';
import { UserGroupModel } from '@app/interface/userGroup.model';
import * as firebase from 'firebase';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { map, mergeAll, mergeMap, switchMap, take } from 'rxjs/operators';
import { FirebaseAuthService } from './firebaseauth.service';
import { AngularFireStorage } from '@angular/fire/storage';

export interface StudyParam {
  action: string;
  study: StudyModel;
}

@Injectable({
  providedIn: 'root',
})
export class StudiesService {
  routeParams$: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(
    private store: AngularFirestore,
    private afAuth: AngularFireAuth,
    private authService: FirebaseAuthService,
    private afStorage: AngularFireStorage
  ) {}

  public addStudy(study: StudyModel): Observable<any> {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        if (user) {
          study.uuid = this.store.createId();
          study.studyName = study.studyName.toLowerCase();
          let resposne = this.store.collection('studies').doc(study.uuid);
          study.createdAt = firebase.default.firestore.Timestamp.now();
          study.updatedAt = firebase.default.firestore.Timestamp.now();
          study.createdBy = user.role;
          study.createdByUserId = user.uuid;
          study.updatedByUserId = null;
          study.docRef = resposne.ref;
          study.isDelete = false;
          resposne.set(study).then(() => {
            this.createDefaultGroupForStudy(study);
          });
          return from(study.uuid);
        }
      })
    );
  }

  public updateStudy(study: StudyModel, uuid: string): Observable<any> {
    return this.authService.getUser().pipe(
      mergeMap((user: UserDetail) => {
        if (user) {
          study.studyName = study.studyName.toLowerCase();
          study.updatedAt = firebase.default.firestore.Timestamp.now();
          if (user.role !== 'admin') {
            study.createdBy = user.role;
          }
          study.updatedByUserId = user.uuid;
          let resposne = this.store
            .collection('studies')
            .doc(uuid)
            .update(study)
            .then(() => {
              let isActive = { isActive: study.isActive };
              Promise.all([
                this.deleteServey(uuid, isActive),
                this.deleteActivty(uuid, isActive),
                this.deleteWellBeing(uuid, isActive),
              ]);
            });
          return from(resposne);
        }
      })
    );
  }

  public getStudies(): Observable<any> {
    return this.authService.getUser().pipe(
      map((user: UserDetail) => {
        if (user) {
          return this.store
            .collection('studies', (ref) => {
              let query: Query = ref;
              if (user.role === 'admin') {
                return query
                  .where('studyAccessType', '!=', 'default')
                  .where('createdBy', '==', 'admin')
                  .where('isDelete', '==', false);
              } else {
                return query
                  .where('studyAccessType', '!=', 'default')
                  .where('isDelete', '==', false)
                  .where('createdBy', 'in', Constant.UserRoles);
              }
            })
            .valueChanges();
        } else {
          return of([]);
        }
      }),
      mergeAll()
    );
  }

  public getDefaultStudies(): Observable<any> {
    return this.authService.getUser().pipe(
      mergeMap((user: UserDetail) => {
        if (user) {
          return this.store
            .collection('studies', (ref) => {
              let query: Query = ref;
              if (user.role === 'admin') {
                return query.where('studyAccessType', '==', 'default').where('createdBy', '==', 'admin');
              } else {
                return query
                  .where('studyAccessType', '==', 'default')
                  .where('createdBy', 'in', ['superadmin', 'admin']);
              }
            })
            .valueChanges();
        } else {
          of([]);
        }
      })
    );
  }

  public getStudyByName(studyName: string): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user) {
          return this.store
            .collection('studies', (ref) => ref.where('studyName', '==', studyName))
            .valueChanges()
            .pipe(map((item) => item[0]));
        } else {
          of([]);
        }
      }),
      take(1)
    );
  }

  public deletStudy(uuid: string): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user) {
          let isDelete = { isDelete: true };
          return this.store
            .collection('studies')
            .doc(uuid)
            .update(isDelete)
            .then(() => {
              Promise.all([
                this.deleteServey(uuid, isDelete),
                this.deleteActivty(uuid, isDelete),
                this.deleteWellBeing(uuid, isDelete),
              ]);
            });
        } else {
          of([]);
        }
      })
    );
  }

  public getSurveyQuestions(surveyUUID: string) {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        return this.store
          .collection<SurveyQuestion>('surveys')
          .doc(surveyUUID)
          .collection('questions', (ref) => ref.orderBy('createdAt', 'desc'))
          .valueChanges();
      })
    );
  }

  public getStudiesBystudy(): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        return this.store
          .collection('studies', (ref) => {
            let query: Query = ref;
            return query.where('studyName', 'in', ['signgaze']);
          })
          .valueChanges();
      })
    );
  }

  public getStudyDetailById(): Observable<any> {
    let uuid = this.getStudyId();
    return this.afAuth.authState.pipe(
      map((user: firebase.default.User) => {
        if (user && uuid) {
          return this.store.collection('studies').doc(uuid).valueChanges();
        } else {
          return of(null);
        }
      }),
      mergeAll()
    );
  }

  public setStudyId(id: string): void {
    localStorage.setItem(Constant.LocalStorageKey.studyId, id);
  }

  public getStudyId(): string {
    return localStorage.getItem(Constant.LocalStorageKey.studyId);
  }

  public deleteServey(studyId: string, params: any): void {
    this.store
      .collection('surveys', (ref: any) => {
        let query: Query = ref;
        return query.where('studyId', '==', studyId);
      })
      .get()
      .toPromise()
      .then((surveys) => {
        if (surveys.empty) {
          return;
        }
        surveys.forEach((survey) => survey.ref.update(params));
      });
  }

  public deleteActivty(studyId: string, params: any): void {
    this.store
      .collection('default-activities', (ref: any) => {
        let query: Query = ref;
        return query.where('studyId', '==', studyId);
      })
      .get()
      .toPromise()
      .then((activities) => {
        if (activities.empty) {
          return;
        }
        activities.forEach((activity) => activity.ref.update(params));
      });
  }

  public deleteWellBeing(studyId: string, params: any): void {
    this.store
      .collection('organisation-wellbeing-activities', (ref: any) => {
        let query: Query = ref;
        return query.where('studyId', '==', studyId);
      })
      .get()
      .toPromise()
      .then((wellbeings) => {
        if (wellbeings.empty) {
          return;
        }
        wellbeings.forEach((wellbeing) => wellbeing.ref.update(params));
      });
  }

  public createDefaultGroupForStudy(study: StudyModel): void {
    let userGroup = {} as UserGroupModel;
    userGroup.name = 'all enrolled users';
    userGroup.description = 'all enrolled users';
    userGroup.studyId = study.uuid;
    userGroup.studyRef = study.docRef;
    userGroup.isDelete = false;
    userGroup.createdAt = study.createdAt;
    userGroup.updatedAt = study.updatedAt;
    userGroup.uuid = this.store.createId();
    userGroup.createdBy = study.createdBy;
    userGroup.createdByUserId = study.createdByUserId;
    userGroup.isDefault = true;
    this.createGroup(userGroup);
  }

  public createGroup(userGroup: UserGroupModel): void {
    this.store.collection('user-groups').doc(userGroup.uuid).set(userGroup, { merge: true });
  }

  public updateConsentStudy(consentForms: consentFormPage[], uuid: string): Observable<any> {
    return this.authService.getUser().pipe(
      map((user: UserDetail) => {
        if (!user) {
          return of(null);
        }
        let resposne = this.store.collection('studies').doc(uuid).update({
          consentFormPages: consentForms,
          updatedAt: firebase.default.firestore.Timestamp.now(),
          createdBy: user.role,
          updatedByUserId: user.uuid,
        });
        return of(resposne);
      }),
      mergeAll()
    );
  }

  public async uploadImage(image: any, studyDetail: StudyModel, field: string): Promise<any> {
    let user = await this.afAuth.currentUser;
    if (!user) {
      return;
    }

    const id = this.store.createId();
    let filePath = `studyImages/${studyDetail.uuid}/${field}/${id}`;
    if (studyDetail[`${field}Path`]) {
      filePath = studyDetail[`${field}Path`];
    }
    const ref = this.afStorage.ref(filePath);
    const task = ref.putString(image, 'data_url');
    const response = await task;
    const data = {
      url: await response.ref.getDownloadURL(),
      fileRef: filePath,
    };
    return data;
  }

  public getStudiesByInvitedAdmin(): Observable<any> {
    return this.authService.getUser().pipe(
      map((user: UserDetail) => {
        if (user) {
          return this.store
            .collection('studies', (ref) => {
              let query: Query = ref;
              if (user.role === 'admin') {
                return query
                  .where('studyAccessType', '!=', 'default')
                  .where('contactEmail', 'array-contains', user.email)
                  .where('isDelete', '==', false);
              } else {
                return query
                  .where('studyAccessType', '!=', 'default')
                  .where('isDelete', '==', false)
                  .where('createdBy', 'in', Constant.UserRoles);
              }
            })
            .valueChanges();
        } else {
          return of([]);
        }
      }),
      mergeAll()
    );
  }

  public updateGroupWithUserStudyId(groups: any, uuid: string): Observable<any> {
    return this.authService.getUser().pipe(
      mergeMap((user: UserDetail) => {
        if (user) {
          let resposne = this.store.collection('studies').doc(uuid).update({
            updatedAt: firebase.default.firestore.Timestamp.now(),
            updatedByUserId: user.uuid,
            userStudyIdWithGroups: groups,
          });
          return from(resposne);
        } else {
          return of([]);
        }
      })
    );
  }
}
